import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getPlatformDbConnectionPressure,
  getPlatformDbLoadCause,
  postPlatformDbReleaseWriterHosts,
} from "../../../api/client";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import { getUserFacingApiMessage } from "../../../utils/getUserFacingApiMessage";

function useConsoleOutlet() {
  const ctx = useOutletContext() || {};
  return {
    registerRefresh: typeof ctx.registerRefresh === "function" ? ctx.registerRefresh : () => {},
    stopRefreshing: typeof ctx.stopRefreshing === "function" ? ctx.stopRefreshing : () => {},
  };
}

const DbConnectionPressurePanel = lazy(() => import("./DbConnectionPressurePanel"));
const DbLoadCausePanel = lazy(() => import("./DbLoadCausePanel"));

const LEVEL_RANK = { critical: 4, warn: 3, elevated: 2, ok: 1, unknown: 0 };
const LEVEL_COLOR = {
  ok: "success",
  elevated: "info",
  warn: "warning",
  critical: "error",
  unknown: "default",
};

function TabPanelSpinner() {
  return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center", alignItems: "center" }} role="status">
      <CircularProgress size={32} />
    </Box>
  );
}

function formatRatio(ratio) {
  if (ratio == null || !Number.isFinite(Number(ratio))) return "—";
  return `${Math.round(Number(ratio) * 1000) / 10}%`;
}

function levelLabel(level, t, prefix) {
  const key = String(level || "unknown");
  return t(`platformPage.${prefix}Level_${key}`, {
    defaultValue:
      key === "ok"
        ? "Normal"
        : key === "elevated"
          ? "Elevado"
          : key === "warn"
            ? "Atenção"
            : key === "critical"
              ? "Crítico"
              : "Desconhecido",
  });
}

function worseLevel(a, b) {
  return (LEVEL_RANK[a] || 0) >= (LEVEL_RANK[b] || 0) ? a : b;
}

function formatMb(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";
  return `${Math.round((n / (1024 * 1024)) * 10) / 10}`;
}

function StatusGlanceCard({
  active,
  title,
  level,
  levelText,
  metric,
  metricHint,
  guidance,
  onClick,
  footer,
}) {
  const interactive = typeof onClick === "function";
  return (
    <Paper
      variant="outlined"
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={(theme) => ({
        p: 2,
        cursor: interactive ? "pointer" : "default",
        height: "100%",
        borderColor: active ? theme.palette.primary.main : "divider",
        bgcolor: active
          ? alpha(theme.palette.primary.main, 0.06)
          : alpha(theme.palette.primary.main, 0.02),
        transition: "border-color 120ms ease, background-color 120ms ease",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        },
      })}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Chip size="small" color={LEVEL_COLOR[level] || "default"} label={levelText} />
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
          {metric}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {metricHint}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {guidance}
        </Typography>
        {footer}
      </Stack>
    </Paper>
  );
}

export default function DbConnectionPressureSectionPage() {
  const { t } = useTranslation("app");
  const { registerRefresh, stopRefreshing } = useConsoleOutlet();
  const [params, setParams] = useSearchParams();
  const [panelKey, setPanelKey] = useState(0);
  const [overview, setOverview] = useState({ load: null, pressure: null });
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState("");
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseNotice, setReleaseNotice] = useState(null);
  const pendingRef = useRef(0);
  const autoPickedRef = useRef(false);

  const view = params.get("view") === "pressure" ? "pressure" : "load";

  const setView = useCallback(
    (next) => {
      const v = next === "pressure" ? "pressure" : "load";
      setParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set("view", v);
          return nextParams;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const [load, pressure] = await Promise.all([
        getPlatformDbLoadCause({ limit: 1 }),
        getPlatformDbConnectionPressure({ limit: 1 }),
      ]);
      setOverview({ load, pressure });
    } catch (err) {
      setOverviewError(
        getUserFacingApiMessage(err)?.message ||
          t("platformPage.dbOpsOverviewError", {
            defaultValue: "Não foi possível carregar o resumo do MySQL.",
          })
      );
    } finally {
      setOverviewLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview, panelKey]);

  useEffect(() => {
    if (autoPickedRef.current || params.get("view") || overviewLoading) return;
    const loadLevel = overview.load?.latest?.level;
    const pressureLevel = overview.pressure?.latest?.level;
    if (!loadLevel && !pressureLevel) return;
    autoPickedRef.current = true;
    const preferPressure =
      (LEVEL_RANK[pressureLevel] || 0) > (LEVEL_RANK[loadLevel] || 0);
    setView(preferPressure ? "pressure" : "load");
  }, [overview, overviewLoading, params, setView]);

  const onPanelSettled = useCallback(() => {
    pendingRef.current = Math.max(0, pendingRef.current - 1);
    if (pendingRef.current === 0) stopRefreshing();
  }, [stopRefreshing]);

  useEffect(() => {
    registerRefresh(() => {
      pendingRef.current = 1;
      setPanelKey((k) => k + 1);
    });
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing]);

  const releaseIdleHosts = useCallback(async () => {
    setReleaseLoading(true);
    setReleaseNotice(null);
    try {
      const result = await postPlatformDbReleaseWriterHosts();
      const after = result?.after || null;
      if (after) {
        setOverview((prev) => ({
          ...prev,
          load: {
            ...(prev.load || {}),
            latest: {
              ...(prev.load?.latest || {}),
              memory: after,
            },
          },
        }));
      }
      setReleaseNotice({
        severity: result?.resolved ? "success" : result?.memoryReleased ? "info" : "warning",
        message: result?.resolved
          ? t("platformPage.dbOpsReleaseHostsResolved", {
              defaultValue:
                "Hosts sem conexão foram removidos e a memória do writer voltou ao normal. Vagas {{before}} → {{after}}, erros por host {{beforeMb}} → {{afterMb}} MB.",
              before: result?.before?.hostSlots ?? "—",
              after: result?.after?.hostSlots ?? "—",
              beforeMb: formatMb(result?.before?.hostErrorBytes),
              afterMb: formatMb(result?.after?.hostErrorBytes),
            })
          : result?.memoryReleased
            ? t("platformPage.dbOpsReleaseHostsPartial", {
                defaultValue:
                  "Hosts sem conexão foram removidos e a memória recuou. Vagas {{before}} → {{after}}, erros por host {{beforeMb}} → {{afterMb}} MB.",
                before: result?.before?.hostSlots ?? "—",
                after: result?.after?.hostSlots ?? "—",
                beforeMb: formatMb(result?.before?.hostErrorBytes),
                afterMb: formatMb(result?.after?.hostErrorBytes),
              })
            : t("platformPage.dbOpsReleaseHostsKept", {
                defaultValue:
                  "Removidos {{hosts}} hosts e {{accounts}} accounts sem conexão. As vagas já reservadas continuam em {{slots}}: o MySQL reaproveita esse bloco e para de crescer com IP novo. A memória só volta ao tamanho inicial no reinício do writer.",
                hosts: result?.idleHosts ?? "—",
                accounts: result?.idleAccounts ?? "—",
                slots: result?.after?.hostSlots ?? "—",
              }),
      });
      setReleaseOpen(false);
    } catch (err) {
      setReleaseNotice({
        severity: "error",
        message:
          getUserFacingApiMessage(err)?.message ||
          t("platformPage.dbOpsReleaseHostsError", {
            defaultValue: "Não foi possível limpar os hosts do writer.",
          }),
      });
      setReleaseOpen(false);
    } finally {
      setReleaseLoading(false);
    }
  }, [t]);

  const loadLatest = overview.load?.latest || null;
  const pressureLatest = overview.pressure?.latest || null;
  const memoryLatest = loadLatest?.memory || null;
  const canReleaseHosts = ["elevated", "warn", "critical"].includes(memoryLatest?.level);
  const combinedLevel = worseLevel(
    worseLevel(loadLatest?.level, pressureLatest?.level),
    memoryLatest?.level
  );

  const situationAlert = useMemo(() => {
    const loadL = loadLatest?.level || "unknown";
    const pressureL = pressureLatest?.level || "unknown";
    const memoryL = memoryLatest?.level || "unknown";
    if (overviewLoading && !loadLatest && !pressureLatest) return null;

    if (
      (LEVEL_RANK[memoryL] || 0) >= 2 &&
      (LEVEL_RANK[memoryL] || 0) >= (LEVEL_RANK[pressureL] || 0) &&
      (LEVEL_RANK[memoryL] || 0) >= (LEVEL_RANK[loadL] || 0)
    ) {
      return {
        severity: memoryL === "critical" ? "error" : memoryL === "warn" ? "warning" : "info",
        title: t("platformPage.dbOpsTriageMemoryTitle", {
          defaultValue: "Prioridade agora: memória do writer",
        }),
        body: t("platformPage.dbOpsTriageMemoryBody", {
          defaultValue:
            "O writer está sem memória ou a tabela de hosts do performance_schema passou do tamanho automático. Carga e conexões podem continuar normais enquanto as telas falham.",
        }),
        actionView: null,
        releaseHosts: true,
      };
    }
    if ((LEVEL_RANK[pressureL] || 0) >= 3 && (LEVEL_RANK[pressureL] || 0) >= (LEVEL_RANK[loadL] || 0)) {
      return {
        severity: pressureL === "critical" ? "error" : "warning",
        title: t("platformPage.dbOpsTriagePressureTitle", {
          defaultValue: "Prioridade agora: teto de conexões",
        }),
        body: t("platformPage.dbOpsTriagePressureBody", {
          defaultValue:
            "O cluster está perto do limite de conexões. Abra Conexões para ver hosts/Lambda em Sleep e liberar slots. Depois, trate retenção de pool na app.",
        }),
        actionView: "pressure",
      };
    }
    if ((LEVEL_RANK[loadL] || 0) >= 2) {
      return {
        severity: loadL === "critical" ? "error" : loadL === "warn" ? "warning" : "info",
        title: t("platformPage.dbOpsTriageLoadTitle", {
          defaultValue: "Prioridade agora: carga / SQL pesado",
        }),
        body: t("platformPage.dbOpsTriageLoadBody", {
          defaultValue:
            "Há pressão de execução no MySQL. Abra Carga e SQL para ver workspace, query ativa e rotas da app — mate o que estiver travado e planeje índice/query no médio prazo.",
        }),
        actionView: "load",
      };
    }
    return {
      severity: "success",
      title: t("platformPage.dbOpsTriageOkTitle", {
        defaultValue: "Situação estável neste momento",
      }),
      body: t("platformPage.dbOpsTriageOkBody", {
        defaultValue:
          "Use o histórico e o SQL lento da app para antecipar regressões. Em incidente AWS (CPU vermelha), comece por Carga e SQL; em “Too many connections”, por Conexões.",
      }),
      actionView: null,
    };
  }, [loadLatest, pressureLatest, memoryLatest, overviewLoading, t]);

  const loadMetric =
    loadLatest?.threadsRunning != null
      ? t("platformPage.dbOpsGlanceLoadMetric", {
          defaultValue: "{{n}} threads em execução",
          n: loadLatest.threadsRunning,
        })
      : "—";
  const loadHint = t("platformPage.dbOpsGlanceLoadHint", {
    defaultValue: "{{q}} queries ativas · top workspace: {{ws}}",
    q:
      loadLatest?.combinedActiveQueries?.length ??
      loadLatest?.instances?.reduce((n, i) => n + (i.activeQueryCount || 0), 0) ??
      "—",
    ws: loadLatest?.combinedTopSchemasByActive?.[0]?.db || "—",
  });
  const pressureMetric = formatRatio(pressureLatest?.ratio);
  const pressureHint = t("platformPage.dbOpsGlancePressureHint", {
    defaultValue: "{{c}} / {{m}} conexões · Sleep {{s}}",
    c: pressureLatest?.threadsConnected ?? "—",
    m: pressureLatest?.maxConnections ?? "—",
    s: pressureLatest?.processlist?.sleepCount ?? "—",
  });

  return (
    <Stack spacing={2.5}>
      <Paper
        variant="outlined"
        sx={(theme) => ({
          p: 2.5,
          borderColor: "divider",
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        })}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t("platformPage.tabDbOps", { defaultValue: "MySQL ops" })}
            </Typography>
            <Chip
              size="small"
              color={LEVEL_COLOR[combinedLevel] || "default"}
              label={t("platformPage.dbOpsCombinedLevel", {
                defaultValue: "Situação geral: {{level}}",
                level: levelLabel(combinedLevel, t, "dbLoad"),
              })}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t("platformPage.dbOpsIntro", {
              defaultValue:
                "Visão rápida da saúde do MySQL da plataforma: o que está pesado agora, o que libera imediatamente, e o que ajustar na aplicação no médio prazo.",
            })}
          </Typography>
        </Stack>
      </Paper>

      {overviewError ? (
        <Alert severity="error" onClose={() => setOverviewError("")}>
          {overviewError}
        </Alert>
      ) : null}

      {situationAlert ? (
        <Alert
          severity={situationAlert.severity}
          action={
            situationAlert.releaseHosts && canReleaseHosts ? (
              <Button
                size="small"
                color="warning"
                variant="outlined"
                disabled={releaseLoading}
                onClick={() => setReleaseOpen(true)}
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("platformPage.dbOpsReleaseHostsButton", {
                  defaultValue: "Limpar hosts ociosos",
                })}
              </Button>
            ) : situationAlert.actionView ? (
              <Chip
                size="small"
                clickable
                color={situationAlert.severity === "error" ? "error" : "default"}
                label={t("platformPage.dbOpsTriageOpen", { defaultValue: "Abrir diagnóstico" })}
                onClick={() => setView(situationAlert.actionView)}
              />
            ) : null
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {situationAlert.title}
          </Typography>
          <Typography variant="body2">{situationAlert.body}</Typography>
        </Alert>
      ) : null}

      {releaseNotice ? (
        <Alert severity={releaseNotice.severity} onClose={() => setReleaseNotice(null)}>
          {releaseNotice.message}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
        }}
      >
        {overviewLoading && !loadLatest && !pressureLatest ? (
          <Box sx={{ gridColumn: "1 / -1", py: 3, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <StatusGlanceCard
              active={view === "load"}
              title={t("platformPage.tabDbLoadCause", { defaultValue: "Carga e SQL" })}
              level={loadLatest?.level || "unknown"}
              levelText={levelLabel(loadLatest?.level, t, "dbLoad")}
              metric={loadMetric}
              metricHint={loadHint}
              guidance={t("platformPage.dbOpsGlanceLoadGuidance", {
                defaultValue:
                  "Quem está consumindo CPU/tempo: workspace, SQL ativo, digests e rotas lentas da app.",
              })}
              onClick={() => setView("load")}
            />
            <StatusGlanceCard
              active={view === "pressure"}
              title={t("platformPage.tabDbPressure", { defaultValue: "Conexões" })}
              level={pressureLatest?.level || "unknown"}
              levelText={levelLabel(pressureLatest?.level, t, "dbPressure")}
              metric={pressureMetric}
              metricHint={pressureHint}
              guidance={t("platformPage.dbOpsGlancePressureGuidance", {
                defaultValue:
                  "Quão perto está o teto de conexões e quais Lambdas/hosts seguram Sleep ocioso.",
              })}
              onClick={() => setView("pressure")}
            />
            <StatusGlanceCard
              active={false}
              title={t("platformPage.dbOpsGlanceMemory", { defaultValue: "Memória do writer" })}
              level={memoryLatest?.level || "unknown"}
              levelText={levelLabel(memoryLatest?.level, t, "dbLoad")}
              metric={t("platformPage.dbOpsGlanceMemoryMetric", {
                defaultValue: "{{n}} vagas de host",
                n: memoryLatest?.hostSlots ?? "—",
              })}
              metricHint={t("platformPage.dbOpsGlanceMemoryHint", {
                defaultValue: "{{seen}} hosts vistos · erros por host {{mb}} MB",
                seen: memoryLatest?.hostsSeen ?? "—",
                mb: formatMb(memoryLatest?.hostErrorBytes),
              })}
              guidance={t("platformPage.dbOpsGlanceMemoryGuidance", {
                defaultValue:
                  "Cresce com cada IP novo de cliente e pode derrubar a tela sem subir thread nem conexão.",
              })}
              footer={
                canReleaseHosts ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={releaseLoading}
                    onClick={(event) => {
                      event.stopPropagation();
                      setReleaseOpen(true);
                    }}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {t("platformPage.dbOpsReleaseHostsButton", {
                      defaultValue: "Limpar hosts ociosos",
                    })}
                  </Button>
                ) : null
              }
            />
          </>
        )}
      </Box>

      <Paper variant="outlined" sx={{ borderColor: "divider", overflow: "hidden" }}>
        <Tabs
          value={view}
          onChange={(_, next) => setView(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            minHeight: 44,
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 44 },
          }}
        >
          <Tab
            value="load"
            label={t("platformPage.tabDbLoadCause", { defaultValue: "Carga e SQL" })}
          />
          <Tab
            value="pressure"
            label={t("platformPage.tabDbPressure", { defaultValue: "Conexões" })}
          />
        </Tabs>

        <Box sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {view === "load"
              ? t("platformPage.dbLoadIntro", {
                  defaultValue:
                    "Identifique o workspace e o SQL que estão pesados agora. Use digests e SQL lento da app para corrigir padrões na estrutura (índices, N+1, rotas quentes).",
                })
              : t("platformPage.dbPressureIntro", {
                  defaultValue:
                    "Veja se o cluster está no limite de conexões. Sleep alto em vários IPs costuma ser pool retido em Lambda — liberar slots agora e reduzir retenção depois.",
                })}
          </Typography>

          <Suspense fallback={<TabPanelSpinner />}>
            {view === "load" ? (
              <DbLoadCausePanel
                key={`load-${panelKey}`}
                onLoaded={onPanelSettled}
                onError={onPanelSettled}
              />
            ) : (
              <DbConnectionPressurePanel
                key={`pressure-${panelKey}`}
                onLoaded={onPanelSettled}
                onError={onPanelSettled}
              />
            )}
          </Suspense>
        </Box>
      </Paper>

      <ConfirmDialog
        open={releaseOpen}
        loading={releaseLoading}
        maxWidth="sm"
        fullWidth
        confirmColor="warning"
        title={t("platformPage.dbOpsReleaseHostsTitle", {
          defaultValue: "Limpar hosts ociosos do writer",
        })}
        confirmText={t("platformPage.dbOpsReleaseHostsConfirm", {
          defaultValue: "Limpar agora",
        })}
        cancelText={t("platformPage.dbOpsReleaseHostsCancel", {
          defaultValue: "Cancelar",
        })}
        content={t("platformPage.dbOpsReleaseHostsBody", {
          defaultValue:
            "Remove só hosts e accounts do performance_schema que não têm conexão aberta. Dados de workspace, sessões ativas e o histórico de SQL da aba Carga e SQL permanecem. O MySQL reaproveita as vagas liberadas, então um IP novo deixa de aumentar a memória. O bloco que o motor já reservou pode continuar igual até o próximo reinício do writer.",
        })}
        onClose={() => {
          if (!releaseLoading) setReleaseOpen(false);
        }}
        onConfirm={releaseIdleHosts}
      />
    </Stack>
  );
}
